import { useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, setDoc, deleteDoc, orderBy, query } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions, WHITELIST_COLLECTION, VIDEOS_COLLECTION, VIDEOS_DOC_ID, TEXTS_COLLECTION, TEXTS_DOC_ID, FINANZAS_TABS, DEFAULT_TAB_TEXTS } from "../firebase";
import { emailKey, isAdmin } from "../auth/AuthGate";

// Callables admin (Cloud Functions en gestion) para gestionar accesos.
const listUsersFn = httpsCallable(functions, "listFinanzasUsers");
const deleteUserFn = httpsCallable(functions, "deleteFinanzasUser");
const unblockUserFn = httpsCallable(functions, "unblockFinanzasUser");
const qAdminFn = httpsCallable(functions, "finanzasQuestionsAdmin");

const fmtD = (ms) => ms ? new Date(ms).toLocaleDateString("es-CL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const C = {
  copper: "#b5836d", copperDark: "#8a5d4a",
  dark: "#2d2a26", muted: "#7a756e",
  bg: "#faf8f6", card: "#fff", line: "#ebe5df",
  green: "#4a9c6d", greenBg: "#edf7f0",
  red: "#c0392b", redBg: "#fdf0ee",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mini editor WYSIWYG: usa contentEditable + document.execCommand.
// Soporta paste de Word/Docs/HTML manteniendo formato básico.
function ToolbarBtn({ onClick, label, title }) {
  return (
    <button type="button" onMouseDown={e => e.preventDefault()} onClick={onClick} title={title}
      style={{
        padding: "4px 8px", border: `1px solid ${C.line}`, borderRadius: "4px",
        background: "#fff", cursor: "pointer", fontFamily: "'DM Sans',sans-serif",
        fontSize: "11px", fontWeight: 700, color: C.dark, minWidth: "28px",
      }}>{label}</button>
  );
}

function RichTextEditor({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const lastSyncedRef = useRef(undefined);

  // Sincroniza value → DOM solo cuando el value externo cambia (no cuando el user tipea).
  useEffect(() => {
    if (ref.current && value !== lastSyncedRef.current) {
      ref.current.innerHTML = value || "";
      lastSyncedRef.current = value;
    }
  }, [value]);

  const handleInput = () => {
    const html = ref.current.innerHTML;
    lastSyncedRef.current = html;
    onChange(html);
  };

  const exec = (cmd, arg) => {
    document.execCommand(cmd, false, arg);
    handleInput();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const html = e.clipboardData.getData("text/html");
    const text = e.clipboardData.getData("text/plain");
    let toInsert;
    if (html) {
      // Limpia bloat de MS Word / Google Docs
      toInsert = html
        .replace(/<style[\s\S]*?<\/style>/gi, "")
        .replace(/<meta[^>]*>/gi, "")
        .replace(/<!--[\s\S]*?-->/g, "")
        .replace(/<\?xml[^>]*\?>/g, "")
        .replace(/style="[^"]*"/gi, "")
        .replace(/class="[^"]*"/gi, "");
    } else {
      toInsert = (text || "").replace(/\n/g, "<br>");
    }
    document.execCommand("insertHTML", false, toInsert);
  };

  return (
    <div style={{ border: `1.5px solid ${C.line}`, borderRadius: "6px", background: "#fdfcfb", overflow: "hidden" }}>
      <div style={{ display: "flex", gap: "3px", padding: "5px", borderBottom: `1px solid ${C.line}`, background: "#fff", flexWrap: "wrap" }}>
        <ToolbarBtn onClick={() => exec("bold")} label="B" title="Negrita (Ctrl+B)" />
        <ToolbarBtn onClick={() => exec("italic")} label={<em>I</em>} title="Cursiva (Ctrl+I)" />
        <ToolbarBtn onClick={() => exec("insertUnorderedList")} label="• Lista" title="Lista con viñetas" />
        <ToolbarBtn onClick={() => exec("insertOrderedList")} label="1. Lista" title="Lista numerada" />
        <ToolbarBtn onClick={() => {
          const url = window.prompt("URL del link:");
          if (url) exec("createLink", url);
        }} label="🔗 Link" title="Insertar link" />
        <ToolbarBtn onClick={() => exec("removeFormat")} label="✕ Formato" title="Quitar formato" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        data-placeholder={placeholder || ""}
        style={{
          minHeight: "120px", padding: "10px 12px",
          fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.dark,
          outline: "none", lineHeight: 1.55, maxHeight: "320px", overflowY: "auto",
        }}
      />
    </div>
  );
}

export default function Admin({ user }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [csvText, setCsvText] = useState("");
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [videos, setVideos] = useState({});
  const [videosBusy, setVideosBusy] = useState(false);
  const [texts, setTexts] = useState({});
  const [textsBusy, setTextsBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [userBusy, setUserBusy] = useState(null); // email en proceso
  const [userFilter, setUserFilter] = useState("");
  const [userOrigin, setUserOrigin] = useState("todos"); // todos | woo | whitelist | bloqueados
  const [adminTab, setAdminTab] = useState("preguntas"); // preguntas | usuarios | contenido
  const allowed = isAdmin(user);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await listUsersFn();
      setUsers(res?.data?.users || []);
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "No se pudo cargar la lista de usuarios." });
    } finally {
      setUsersLoading(false);
    }
  };

  const handleDeleteUser = async (email) => {
    if (!window.confirm(`¿Quitar el acceso de ${email}?\n\nSe borra su suscripción y se bloquea para que no vuelva a entrar (ni siquiera con una compra Woo). Lo podés desbloquear después.`)) return;
    setMsg(null); setUserBusy(email);
    try {
      const res = await deleteUserFn({ email });
      setMsg({ type: "ok", text: `Acceso revocado: ${email}${res?.data?.deletedSubs ? ` (${res.data.deletedSubs} sub borrada/s)` : ""}` });
      await loadUsers();
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: e?.message || "No se pudo revocar el acceso." });
    } finally { setUserBusy(null); }
  };

  const handleUnblockUser = async (email) => {
    setMsg(null); setUserBusy(email);
    try {
      await unblockUserFn({ email });
      setMsg({ type: "ok", text: `Desbloqueado: ${email}. Si tiene compra Woo activa, el sistema lo reactiva; si no, agregalo a la whitelist.` });
      await loadUsers();
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: e?.message || "No se pudo desbloquear." });
    } finally { setUserBusy(null); }
  };

  // ===== Preguntas de alumnos =====
  const [questions, setQuestions] = useState([]);
  const [qPending, setQPending] = useState(0);
  const [qLoading, setQLoading] = useState(true);
  const [qOpenId, setQOpenId] = useState(null);
  const [qThread, setQThread] = useState(null);
  const [qThreadLoading, setQThreadLoading] = useState(false);
  const [qAnswer, setQAnswer] = useState("");
  const [qBusy, setQBusy] = useState(false);
  const [qSuggesting, setQSuggesting] = useState(false);

  const loadQuestions = async () => {
    setQLoading(true);
    try {
      const r = await qAdminFn({ action: "list" });
      setQuestions(r.data?.items || []);
      setQPending(r.data?.pending || 0);
    } catch (e) { console.error(e); }
    finally { setQLoading(false); }
  };

  const openQ = async (id) => {
    setQOpenId(id); setQThread(null); setQThreadLoading(true); setQAnswer("");
    try {
      const r = await qAdminFn({ action: "thread", questionId: id });
      setQThread(r.data);
    } catch (e) { console.error(e); setMsg({ type: "error", text: "No se pudo abrir la pregunta." }); setQOpenId(null); }
    finally { setQThreadLoading(false); }
  };

  const sendAnswer = async () => {
    if (!qAnswer.trim()) return;
    setQBusy(true); setMsg(null);
    try {
      await qAdminFn({ action: "answer", questionId: qOpenId, text: qAnswer.trim() });
      setQAnswer("");
      await openQ(qOpenId); await loadQuestions();
      setMsg({ type: "ok", text: "Respuesta enviada (le llegó un mail al alumno)." });
    } catch (e) { console.error(e); setMsg({ type: "error", text: e?.message || "No se pudo enviar." }); }
    finally { setQBusy(false); }
  };

  const suggestAnswer = async () => {
    setQSuggesting(true); setMsg(null);
    try {
      const r = await qAdminFn({ action: "suggest", questionId: qOpenId });
      setQAnswer(r.data?.suggestion || "");
    } catch (e) { console.error(e); setMsg({ type: "error", text: e?.message || "No se pudo generar la sugerencia." }); }
    finally { setQSuggesting(false); }
  };

  const togglePublic = async (id, current) => {
    try {
      await qAdminFn({ action: "setPublic", questionId: id, isPublic: !current });
      await loadQuestions();
      if (qThread && qOpenId === id) setQThread(t => ({ ...t, question: { ...t.question, isPublic: !current } }));
    } catch (e) { console.error(e); setMsg({ type: "error", text: "No se pudo cambiar la visibilidad." }); }
  };

  const deleteQ = async (id) => {
    if (!window.confirm("¿Borrar esta pregunta y todo su hilo?")) return;
    try {
      await qAdminFn({ action: "delete", questionId: id });
      setQOpenId(null); setQThread(null);
      await loadQuestions();
    } catch (e) { console.error(e); setMsg({ type: "error", text: "No se pudo borrar." }); }
  };

  const loadVideos = async () => {
    try {
      const snap = await getDoc(doc(db, VIDEOS_COLLECTION, VIDEOS_DOC_ID));
      setVideos(snap.exists() ? snap.data() : {});
    } catch (e) { console.error(e); }
  };

  const loadTexts = async () => {
    try {
      const snap = await getDoc(doc(db, TEXTS_COLLECTION, TEXTS_DOC_ID));
      // Si no hay nada en Firestore, pre-popular con los defaults para que el admin los edite
      setTexts(snap.exists() ? snap.data() : { ...DEFAULT_TAB_TEXTS });
    } catch (e) { console.error(e); }
  };

  const handleVideoChange = (key, value) => setVideos(v => ({ ...v, [key]: value }));
  const handleTextChange = (key, value) => setTexts(t => ({ ...t, [key]: value }));

  const saveVideos = async () => {
    setMsg(null); setVideosBusy(true);
    try {
      const cleaned = {};
      FINANZAS_TABS.forEach(t => {
        const v = (videos[t.key] || "").trim();
        if (v) cleaned[t.key] = v;
      });
      await setDoc(doc(db, VIDEOS_COLLECTION, VIDEOS_DOC_ID), cleaned, { merge: false });
      setMsg({ type: "ok", text: "Videos guardados ✓" });
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "No se pudieron guardar los videos." });
    } finally { setVideosBusy(false); }
  };

  const saveTexts = async () => {
    setMsg(null); setTextsBusy(true);
    try {
      const cleaned = {};
      FINANZAS_TABS.forEach(t => {
        const v = (texts[t.key] || "").trim();
        if (v) cleaned[t.key] = v;
      });
      await setDoc(doc(db, TEXTS_COLLECTION, TEXTS_DOC_ID), cleaned, { merge: false });
      setMsg({ type: "ok", text: "Textos guardados ✓" });
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "No se pudieron guardar los textos." });
    } finally { setTextsBusy(false); }
  };

  const resetTextToDefault = (key) => {
    if (window.confirm(`¿Restaurar el texto de "${FINANZAS_TABS.find(t => t.key === key)?.label}" al default?`)) {
      setTexts(t => ({ ...t, [key]: DEFAULT_TAB_TEXTS[key] || "" }));
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, WHITELIST_COLLECTION), orderBy("email")));
      setRows(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "Error cargando la lista." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (allowed) { load(); loadVideos(); loadTexts(); loadUsers(); loadQuestions(); } }, [allowed]);

  if (!allowed) return <Navigate to="/" replace />;

  const addOne = async (emailRaw) => {
    const email = emailKey(emailRaw);
    if (!EMAIL_RE.test(email)) return { ok: false, reason: "inválido" };
    try {
      await setDoc(doc(db, WHITELIST_COLLECTION, email), {
        email,
        addedAt: Date.now(),
        addedBy: user.email,
      }, { merge: true });
      return { ok: true };
    } catch (e) {
      console.error(e);
      return { ok: false, reason: e.message };
    }
  };

  const handleAdd = async () => {
    setMsg(null);
    setBusy(true);
    const r = await addOne(newEmail);
    setBusy(false);
    if (r.ok) {
      setMsg({ type: "ok", text: `Agregado: ${emailKey(newEmail)}` });
      setNewEmail("");
      load();
    } else {
      setMsg({ type: "error", text: r.reason === "inválido" ? "Email inválido." : "No se pudo agregar." });
    }
  };

  const handleDelete = async (email) => {
    if (!window.confirm(`¿Quitar ${email} de la lista?`)) return;
    try {
      await deleteDoc(doc(db, WHITELIST_COLLECTION, email));
      load();
    } catch (e) {
      console.error(e);
      setMsg({ type: "error", text: "No se pudo eliminar." });
    }
  };

  const handleCsv = async () => {
    setMsg(null);
    const lines = csvText.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean);
    if (lines.length === 0) { setMsg({ type: "error", text: "Pegá al menos un email." }); return; }
    setBusy(true);
    let ok = 0, bad = 0;
    for (const line of lines) {
      const r = await addOne(line);
      if (r.ok) ok++; else bad++;
    }
    setBusy(false);
    setCsvText("");
    setMsg({ type: ok > 0 ? "ok" : "error", text: `${ok} agregados${bad > 0 ? ` · ${bad} con error` : ""}.` });
    load();
  };

  const handleCsvFile = async (file) => {
    const text = await file.text();
    setCsvText(text);
  };

  return (
    <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'Libre Baskerville','Georgia',serif", padding: "20px" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=DM+Sans:wght@400;500;600;700&display=swap');`}</style>

      <div style={{ maxWidth: "680px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "18px" }}>
          <Link to="/" style={{ color: C.copper, textDecoration: "none", fontFamily: "'DM Sans',sans-serif", fontSize: "13px", fontWeight: 600 }}>← Volver</Link>
          <h1 style={{ margin: 0, fontSize: "22px", color: C.dark }}>Admin</h1>
          <span style={{ width: "60px" }} />
        </div>

        {msg && (
          <div style={{
            padding: "10px 12px", borderRadius: "8px", marginBottom: "14px",
            background: msg.type === "ok" ? C.greenBg : C.redBg,
            color: msg.type === "ok" ? C.green : C.red,
            fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 600,
          }}>{msg.text}</div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: "6px", marginBottom: "16px", flexWrap: "wrap" }}>
          {[
            { k: "preguntas", label: "💬 Preguntas" },
            { k: "usuarios", label: "👤 Usuarios" },
            { k: "contenido", label: "📚 Contenido" },
          ].map(t => (
            <button key={t.k} onClick={() => setAdminTab(t.k)} style={{
              padding: "9px 18px", borderRadius: "10px", cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "13px", fontWeight: 700,
              border: `1px solid ${adminTab === t.k ? C.copper : C.line}`,
              background: adminTab === t.k ? C.copper : "#fff",
              color: adminTab === t.k ? "#fff" : C.muted,
            }}>{t.label}{t.k === "preguntas" && qPending > 0 ? ` · ${qPending}` : ""}</button>
          ))}
        </div>

        {/* Preguntas de alumnos */}
        {adminTab === "preguntas" && (
        <div style={{ background: C.card, borderRadius: "12px", padding: "16px", border: `1px solid ${C.line}`, marginBottom: "14px" }}>
          {!qOpenId ? (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
                <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif" }}>
                  Preguntas de alumnos {qPending > 0 && <span style={{ color: C.red }}>· {qPending} pendiente{qPending > 1 ? "s" : ""}</span>}
                </div>
                <button onClick={loadQuestions} style={{
                  padding: "4px 10px", border: `1px solid ${C.line}`, borderRadius: "6px", cursor: "pointer",
                  fontFamily: "'DM Sans',sans-serif", fontSize: "10px", fontWeight: 600, color: C.muted, background: "#fff",
                }}>↻ Refrescar</button>
              </div>

              {qLoading ? (
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, textAlign: "center", padding: "20px" }}>Cargando…</div>
              ) : questions.length === 0 ? (
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, textAlign: "center", padding: "20px" }}>Sin preguntas todavía.</div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "360px", overflowY: "auto" }}>
                  {questions.map((q) => (
                    <div key={q.id} style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
                      padding: "9px 11px", borderRadius: "8px", border: `1px solid ${C.line}`,
                      background: q.status === "pending" ? "#fff8f3" : "#fdfcfb", fontFamily: "'DM Sans',sans-serif",
                    }}>
                      <button onClick={() => openQ(q.id)} style={{ background: "none", border: "none", textAlign: "left", cursor: "pointer", minWidth: 0, flex: 1, padding: 0 }}>
                        <div style={{ fontSize: "13px", fontWeight: 600, color: C.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{q.subject}</div>
                        <div style={{ fontSize: "10.5px", color: C.muted, marginTop: "2px" }}>
                          {q.studentEmail} · {fmtD(q.lastMessageAt)}
                        </div>
                      </button>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {q.isPublic && <span style={{ padding: "1px 7px", borderRadius: "10px", fontSize: "9px", fontWeight: 700, color: "#5a6a7a", background: "#eaeef2" }}>FAQ</span>}
                        <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "9px", fontWeight: 700, color: q.status === "answered" ? C.green : C.copperDark, background: q.status === "answered" ? C.greenBg : "#f3e9e3" }}>
                          {q.status === "answered" ? "Resp." : "Pend."}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <button onClick={() => { setQOpenId(null); setQThread(null); }} style={{ background: "none", border: "none", cursor: "pointer", color: C.copper, fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 600, padding: 0, marginBottom: "10px" }}>← Volver a preguntas</button>
              {qThreadLoading || !qThread ? (
                <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, textAlign: "center", padding: "20px" }}>Cargando…</div>
              ) : (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "10px", marginBottom: "10px" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: "15px", fontWeight: 700, color: C.dark, fontFamily: "'DM Sans',sans-serif" }}>{qThread.question.subject}</div>
                      <div style={{ fontSize: "11px", color: C.muted, fontFamily: "'DM Sans',sans-serif" }}>{qThread.question.studentName} · {qThread.question.studentEmail}</div>
                    </div>
                    <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                      <button onClick={() => togglePublic(qThread.question.id, qThread.question.isPublic)} title="Mostrar en Preguntas frecuentes" style={{
                        padding: "4px 10px", borderRadius: "6px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "10px", fontWeight: 700,
                        border: `1px solid ${qThread.question.isPublic ? "#5a6a7a" : C.line}`, background: qThread.question.isPublic ? "#eaeef2" : "#fff", color: qThread.question.isPublic ? "#5a6a7a" : C.muted,
                      }}>{qThread.question.isPublic ? "✓ Pública (FAQ)" : "Hacer pública"}</button>
                      <button onClick={() => deleteQ(qThread.question.id)} style={{ padding: "4px 9px", borderRadius: "6px", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "11px", fontWeight: 700, border: `1px solid ${C.red}44`, background: C.redBg, color: C.red }}>✕</button>
                    </div>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "12px", maxHeight: "300px", overflowY: "auto" }}>
                    {qThread.messages.map((m) => {
                      const prof = m.authorType === "professor";
                      return (
                        <div key={m.id} style={{
                          padding: "10px 12px", borderRadius: "8px", fontFamily: "'DM Sans',sans-serif",
                          background: prof ? C.greenBg : "#fdfcfb", border: `1px solid ${prof ? C.green + "44" : C.line}`,
                          marginLeft: prof ? "20px" : 0, marginRight: prof ? 0 : "20px",
                        }}>
                          <div style={{ fontSize: "10px", fontWeight: 700, color: prof ? C.green : C.copperDark, marginBottom: "3px" }}>
                            {prof ? "Profesor" : (qThread.question.studentName || "Alumno")} · <span style={{ color: C.muted, fontWeight: 500 }}>{fmtD(m.createdAt)}</span>
                          </div>
                          <div style={{ fontSize: "13px", color: C.dark, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{m.text}</div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em" }}>Tu respuesta</div>
                    <button onClick={suggestAnswer} disabled={qSuggesting} style={{
                      padding: "4px 12px", borderRadius: "14px", cursor: qSuggesting ? "wait" : "pointer", fontFamily: "'DM Sans',sans-serif", fontSize: "11px", fontWeight: 700,
                      border: `1px solid ${C.copper}`, background: "#fff", color: C.copperDark, opacity: qSuggesting ? 0.6 : 1,
                    }}>{qSuggesting ? "Pensando…" : "✨ Sugerir con IA"}</button>
                  </div>
                  <textarea value={qAnswer} onChange={(e) => setQAnswer(e.target.value)} rows={5}
                    placeholder="Escribí tu respuesta (o generala con IA y editala)…"
                    style={{ width: "100%", padding: "10px 12px", border: `1.5px solid ${C.line}`, borderRadius: "8px", fontFamily: "'DM Sans',sans-serif", fontSize: "13px", color: C.dark, background: "#fdfcfb", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "8px" }}>
                    <button onClick={sendAnswer} disabled={qBusy || !qAnswer.trim()} style={{
                      padding: "10px 20px", border: "none", borderRadius: "8px", cursor: qBusy || !qAnswer.trim() ? "not-allowed" : "pointer",
                      fontFamily: "'DM Sans',sans-serif", fontSize: "13px", fontWeight: 700,
                      background: `linear-gradient(135deg, ${C.copper}, ${C.copperDark})`, color: "#fff", opacity: qBusy || !qAnswer.trim() ? 0.5 : 1,
                    }}>{qBusy ? "Enviando…" : "Enviar respuesta"}</button>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        )}

        {/* Usuarios con acceso (suscriptores Woo + whitelist) */}
        {adminTab === "usuarios" && (<>
        <div style={{ background: C.card, borderRadius: "12px", padding: "16px", border: `1px solid ${C.line}`, marginBottom: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif" }}>
              Usuarios con acceso ({users.length})
            </div>
            <button onClick={loadUsers} style={{
              padding: "4px 10px", border: `1px solid ${C.line}`, borderRadius: "6px", cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "10px", fontWeight: 600, color: C.muted, background: "#fff",
            }}>↻ Refrescar</button>
          </div>
          <div style={{ fontSize: "11px", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginBottom: "10px" }}>
            Compradores Woo y emails de la whitelist. La <strong>✕</strong> revoca el acceso y bloquea el email (no vuelve aunque tenga compra). Lo podés desbloquear.
          </div>

          <input type="text" value={userFilter} onChange={e => setUserFilter(e.target.value)}
            placeholder="Buscar por email…"
            style={{
              width: "100%", padding: "8px 10px", border: `1.5px solid ${C.line}`, borderRadius: "8px",
              fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.dark, background: "#fdfcfb",
              outline: "none", boxSizing: "border-box", marginBottom: "8px",
            }} />

          <div style={{ display: "flex", gap: "6px", marginBottom: "10px", flexWrap: "wrap" }}>
            {[
              { k: "todos", label: "Todos" },
              { k: "woo", label: "Woo" },
              { k: "whitelist", label: "Whitelist" },
              { k: "bloqueados", label: "Bloqueados" },
            ].map(opt => (
              <button key={opt.k} onClick={() => setUserOrigin(opt.k)} style={{
                padding: "4px 12px", borderRadius: "14px", cursor: "pointer",
                fontFamily: "'DM Sans',sans-serif", fontSize: "11px", fontWeight: 700,
                border: `1px solid ${userOrigin === opt.k ? C.copper : C.line}`,
                background: userOrigin === opt.k ? C.copper : "#fff",
                color: userOrigin === opt.k ? "#fff" : C.muted,
              }}>{opt.label}</button>
            ))}
          </div>

          {usersLoading ? (
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, textAlign: "center", padding: "20px" }}>Cargando…</div>
          ) : (() => {
            const f = userFilter.trim().toLowerCase();
            const visible = users
              .filter(u => !f || u.email.includes(f))
              .filter(u => userOrigin === "todos"
                || (userOrigin === "woo" && u.sources.includes("woo"))
                || (userOrigin === "whitelist" && u.sources.includes("whitelist"))
                || (userOrigin === "bloqueados" && u.blocked))
              .sort((a, b) => (b.subDate || 0) - (a.subDate || 0) || a.email.localeCompare(b.email));
            if (visible.length === 0) {
              return <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, textAlign: "center", padding: "20px" }}>
                {users.length === 0 ? "Sin usuarios todavía." : "Ningún email coincide."}
              </div>;
            }
            const badge = (text, fg, bg) => (
              <span style={{ padding: "1px 7px", borderRadius: "10px", fontSize: "9px", fontWeight: 700, color: fg, background: bg, textTransform: "uppercase", letterSpacing: "0.04em" }}>{text}</span>
            );
            return (
              <div style={{ display: "flex", flexDirection: "column", gap: "1px", maxHeight: "360px", overflowY: "auto" }}>
                {visible.map((u, i) => (
                  <div key={u.email} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px",
                    padding: "8px 10px", borderRadius: "6px", background: u.blocked ? C.redBg : (i % 2 === 0 ? "#fdfcfb" : "#fff"),
                    fontFamily: "'DM Sans',sans-serif",
                  }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "3px", minWidth: 0 }}>
                      <span style={{ fontSize: "13px", color: C.dark, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</span>
                      <div style={{ display: "flex", gap: "5px", flexWrap: "wrap" }}>
                        {u.sources.includes("woo") && badge("Woo", C.copperDark, "#f3e9e3")}
                        {u.sources.includes("whitelist") && badge("Whitelist", "#5a6a7a", "#eaeef2")}
                        {u.blocked && badge("Bloqueado", C.red, "#fadbd6")}
                        {!u.blocked && u.status && u.status !== "active" && badge(u.status, C.muted, "#eee")}
                      </div>
                      {(u.subDate || u.orderNumber) && (
                        <div style={{ fontSize: "10.5px", color: C.muted, display: "flex", gap: "12px", flexWrap: "wrap" }}>
                          {u.subDate && <span>📅 {new Date(u.subDate).toLocaleDateString("es-CL")}</span>}
                          {u.orderNumber && <span>🛒 Orden #{u.orderNumber}</span>}
                        </div>
                      )}
                    </div>
                    {u.blocked ? (
                      <button onClick={() => handleUnblockUser(u.email)} disabled={userBusy === u.email} style={{
                        padding: "5px 10px", border: `1px solid ${C.green}55`, borderRadius: "6px", cursor: userBusy === u.email ? "wait" : "pointer",
                        fontSize: "11px", fontWeight: 600, color: C.green, background: "#edf7f0", fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap",
                        opacity: userBusy === u.email ? 0.5 : 1,
                      }}>Desbloquear</button>
                    ) : (
                      <button onClick={() => handleDeleteUser(u.email)} disabled={userBusy === u.email} title="Revocar acceso y bloquear" style={{
                        padding: "5px 11px", border: `1px solid ${C.red}44`, borderRadius: "6px", cursor: userBusy === u.email ? "wait" : "pointer",
                        fontSize: "13px", fontWeight: 700, color: C.red, background: C.redBg, fontFamily: "'DM Sans',sans-serif",
                        opacity: userBusy === u.email ? 0.5 : 1,
                      }}>✕</button>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>

        {/* Add one */}
        <div style={{ background: C.card, borderRadius: "12px", padding: "16px", border: `1px solid ${C.line}`, marginBottom: "14px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif", marginBottom: "10px" }}>
            Agregar email
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)}
              placeholder="alumno@ejemplo.com"
              onKeyDown={e => { if (e.key === "Enter") handleAdd(); }}
              style={{
                flex: 1, padding: "10px 12px", border: `1.5px solid ${C.line}`, borderRadius: "8px",
                fontFamily: "'DM Sans',sans-serif", fontSize: "13px", color: C.dark, background: "#fdfcfb", outline: "none",
              }} />
            <button onClick={handleAdd} disabled={busy} style={{
              padding: "10px 18px", border: "none", borderRadius: "8px", cursor: busy ? "wait" : "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "13px", fontWeight: 700,
              background: `linear-gradient(135deg, ${C.copper}, ${C.copperDark})`, color: "#fff",
              opacity: busy ? 0.6 : 1,
            }}>Agregar</button>
          </div>
        </div>

        {/* CSV bulk */}
        <div style={{ background: C.card, borderRadius: "12px", padding: "16px", border: `1px solid ${C.line}`, marginBottom: "14px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif", marginBottom: "4px" }}>
            Importar en lote
          </div>
          <div style={{ fontSize: "11px", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginBottom: "10px" }}>
            Pegá emails (uno por línea, separados por coma o punto y coma) o subí un CSV.
          </div>
          <textarea value={csvText} onChange={e => setCsvText(e.target.value)}
            placeholder={"alumno1@ejemplo.com\nalumno2@ejemplo.com\n..."}
            rows={5}
            style={{
              width: "100%", padding: "10px 12px", border: `1.5px solid ${C.line}`, borderRadius: "8px",
              fontFamily: "'DM Sans',monospace", fontSize: "12px", color: C.dark, background: "#fdfcfb",
              outline: "none", resize: "vertical", boxSizing: "border-box",
            }} />
          <div style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "center" }}>
            <label style={{
              padding: "8px 14px", border: `1.5px solid ${C.line}`, borderRadius: "8px", cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 600, color: C.muted, background: "#fff",
            }}>
              Subir CSV
              <input type="file" accept=".csv,.txt" onChange={e => { if (e.target.files[0]) handleCsvFile(e.target.files[0]); e.target.value = ""; }} style={{ display: "none" }} />
            </label>
            <button onClick={handleCsv} disabled={busy || !csvText.trim()} style={{
              padding: "8px 14px", border: "none", borderRadius: "8px",
              cursor: busy || !csvText.trim() ? "not-allowed" : "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 700,
              background: C.dark, color: "#fff",
              opacity: busy || !csvText.trim() ? 0.5 : 1,
            }}>Importar</button>
          </div>
        </div>

        </>)}

        {/* Videos por sección */}
        {adminTab === "contenido" && (<>
        <div style={{ background: C.card, borderRadius: "12px", padding: "16px", border: `1px solid ${C.line}`, marginBottom: "14px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif", marginBottom: "4px" }}>
            Videos por sección
          </div>
          <div style={{ fontSize: "11px", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginBottom: "12px" }}>
            Pegá el link de YouTube (cualquier formato: <code>youtu.be/XXX</code>, <code>youtube.com/watch?v=XXX</code>) o solo el ID. Dejá vacío para no mostrar video en esa sección.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {FINANZAS_TABS.map(tab => (
              <div key={tab.key} style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "8px", alignItems: "center" }}>
                <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 700, color: C.dark }}>
                  {tab.label}
                </label>
                <input type="text"
                  value={videos[tab.key] || ""}
                  onChange={e => handleVideoChange(tab.key, e.target.value)}
                  placeholder="https://youtu.be/... o ID de 11 caracteres"
                  style={{
                    width: "100%", padding: "8px 10px", border: `1.5px solid ${C.line}`, borderRadius: "6px",
                    fontFamily: "'DM Sans',monospace", fontSize: "12px", color: C.dark, background: "#fdfcfb", outline: "none",
                    boxSizing: "border-box",
                  }} />
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "12px" }}>
            <button onClick={saveVideos} disabled={videosBusy} style={{
              padding: "8px 18px", border: "none", borderRadius: "8px", cursor: videosBusy ? "wait" : "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 700,
              background: `linear-gradient(135deg, ${C.copper}, ${C.copperDark})`, color: "#fff",
              opacity: videosBusy ? 0.6 : 1,
            }}>{videosBusy ? "Guardando…" : "Guardar videos"}</button>
          </div>
        </div>

        {/* Textos explicativos por sección */}
        <div style={{ background: C.card, borderRadius: "12px", padding: "16px", border: `1px solid ${C.line}`, marginBottom: "14px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif", marginBottom: "4px" }}>
            Textos explicativos por sección
          </div>
          <div style={{ fontSize: "11px", color: C.muted, fontFamily: "'DM Sans',sans-serif", marginBottom: "12px" }}>
            Texto que aparece arriba de cada herramienta (bajo el video) en un bloque "📖 Sobre esta sección". Vacío = se muestra el default del código.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {FINANZAS_TABS.map(tab => (
              <div key={tab.key}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: "4px" }}>
                  <label style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 700, color: C.dark }}>
                    {tab.label}
                  </label>
                  <button onClick={() => resetTextToDefault(tab.key)} title="Restaurar al texto default"
                    style={{
                      padding: "2px 8px", border: `1px solid ${C.line}`, borderRadius: "4px", cursor: "pointer",
                      fontFamily: "'DM Sans',sans-serif", fontSize: "10px", fontWeight: 600,
                      background: "#fff", color: C.muted,
                    }}>↺ default</button>
                </div>
                <RichTextEditor
                  value={texts[tab.key] || ""}
                  onChange={v => handleTextChange(tab.key, v)}
                  placeholder={`Texto explicativo de ${tab.label}…`}
                />
              </div>
            ))}
          </div>
          <div style={{ marginTop: "8px", padding: "8px 10px", borderRadius: "6px", background: C.bg, fontFamily: "'DM Sans',sans-serif", fontSize: "10px", color: C.muted, lineHeight: 1.5 }}>
            💡 Podés pegar texto formateado desde Word, Google Docs o una página web — mantiene el formato. La barra te deja poner negrita, cursiva, listas y links.
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "14px" }}>
            <button onClick={saveTexts} disabled={textsBusy} style={{
              padding: "8px 18px", border: "none", borderRadius: "8px", cursor: textsBusy ? "wait" : "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "12px", fontWeight: 700,
              background: `linear-gradient(135deg, ${C.copper}, ${C.copperDark})`, color: "#fff",
              opacity: textsBusy ? 0.6 : 1,
            }}>{textsBusy ? "Guardando…" : "Guardar textos"}</button>
          </div>
        </div>

        </>)}

        {/* List */}
        {adminTab === "usuarios" && (
        <div style={{ background: C.card, borderRadius: "12px", padding: "16px", border: `1px solid ${C.line}` }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: C.copper, textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "'DM Sans',sans-serif" }}>
              Whitelist ({rows.length})
            </div>
            <button onClick={load} style={{
              padding: "4px 10px", border: `1px solid ${C.line}`, borderRadius: "6px", cursor: "pointer",
              fontFamily: "'DM Sans',sans-serif", fontSize: "10px", fontWeight: 600, color: C.muted, background: "#fff",
            }}>↻ Refrescar</button>
          </div>

          {loading ? (
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, textAlign: "center", padding: "20px" }}>Cargando…</div>
          ) : rows.length === 0 ? (
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "12px", color: C.muted, textAlign: "center", padding: "20px" }}>
              Sin emails en la lista todavía.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
              {rows.map((r, i) => (
                <div key={r.id} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "8px 10px", borderRadius: "6px", background: i % 2 === 0 ? "#fdfcfb" : "#fff",
                  fontFamily: "'DM Sans',sans-serif",
                }}>
                  <span style={{ fontSize: "13px", color: C.dark, fontWeight: 500 }}>{r.email || r.id}</span>
                  <button onClick={() => handleDelete(r.id)} style={{
                    padding: "4px 10px", border: `1px solid ${C.red}44`, borderRadius: "6px", cursor: "pointer",
                    fontSize: "11px", fontWeight: 600, color: C.red, background: C.redBg, fontFamily: "'DM Sans',sans-serif",
                  }}>Quitar</button>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
